import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, pairwise, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';
import { IWidgetRequestAddPayload } from '../widget-requests.interface';
import {
  ERestrictAccessBy,
  EWidgetRequestResultType,
  IWidgetRequestsAddEditDialogData,
} from './widget-requests-add-edit-dialog.interface';
import { WidgetRequestsAddEditDialogStore } from './widget-requests-add-edit-dialog.store';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslatePipe,
    MatDropdown,
    MatHint,
    MatIcon,
  ],
  providers: [WidgetRequestsAddEditDialogStore],
  selector: 'app-widget-requests-add-edit-dialog',
  styleUrl: './widget-requests-add-edit-dialog.component.scss',
  templateUrl: './widget-requests-add-edit-dialog.component.html',
})
export class WidgetRequestsAddEditDialogComponent {
  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    restoreFocus: false,
    width: EDialogWidth.xSmall,
  };

  public readonly injectData = inject<IWidgetRequestsAddEditDialogData>(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  public readonly store = inject(WidgetRequestsAddEditDialogStore);

  public readonly requestMethodDropdownOptions = [
    {
      id: 1,
      name: 'GET',
    },
    {
      id: 2,
      name: 'POST',
    },
  ];

  public readonly restrictAccessByDropdownOptions: IDropdownOption<ERestrictAccessBy>[] = [
    {
      id: ERestrictAccessBy.none,
      name: this.translate.instant('field.noRestriction'),
    },
    {
      id: ERestrictAccessBy.workspace,
      name: this.translate.instant('field.workspace'),
    },
    {
      id: ERestrictAccessBy.dashboard,
      name: this.translate.instant('field.dashboard'),
    },
  ];

  public readonly resultTypeDropdownOptions: IDropdownOption<EWidgetRequestResultType>[] = Object.values(
    EWidgetRequestResultType,
  ).map((type) => ({
    id: type,
    name: this.translate.instant(`field.${type}`),
  }));

  public readonly addEditForm = new FormGroup({
    content: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    dashboardId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    dataSourceId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    endpoint: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(2048)],
    }),
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    requestMethod: new FormControl<IDropdownOption[]>([this.requestMethodDropdownOptions[0]], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    restrictAccessBy: new FormControl<IDropdownOption<ERestrictAccessBy>[]>([this.restrictAccessByDropdownOptions[0]], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    resultType: new FormControl<IDropdownOption<EWidgetRequestResultType>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    workspaceId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  public readonly requestMethodDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.requestMethod'),
  };

  public readonly restrictAccessByDropdownSettings: IDropdownSettings = {
    ...this.requestMethodDropdownSettings,
    text: this.translate.instant('field.restrictAccessBy'),
  };

  public readonly resultTypeDropdownSettings: IDropdownSettings = {
    ...this.requestMethodDropdownSettings,
    text: this.translate.instant('field.resultType'),
  };

  public readonly dataSourceDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.dataSource'),
  };

  public readonly dashboardDropdownSettings: IDropdownSettings = {
    ...this.dataSourceDropdownSettings,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    singleSelection: false,
    text: this.translate.instant('field.dashboard'),
  };

  public readonly workspaceDropdownSettings: IDropdownSettings = {
    ...this.dataSourceDropdownSettings,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    singleSelection: false,
    text: this.translate.instant('field.workspace'),
  };

  public readonly dashboardSearch = new BehaviorSubject('');
  public readonly workspaceSearch = new BehaviorSubject('');

  public readonly ERestrictAccessBy = ERestrictAccessBy;
  public readonly EResultType = EWidgetRequestResultType;

  constructor() {
    this.availabilityOfRestrictAccessInputsSubscription();
    this.availabilityOfContentPathInputSubscription();

    const { editData } = this.injectData;

    if (editData) {
      this.addEditForm.reset({
        content: editData.content ?? '',
        dashboardId:
          editData.authorization?.scope === ERestrictAccessBy.dashboard
            ? editData.authorization.ids.map((id) => ({ id, name: '...' }))
            : [],
        dataSourceId: [{ id: editData.dataSourceId, name: '...' }],
        endpoint: editData.endpoint,
        name: editData.name,
        requestMethod: this.requestMethodDropdownOptions.filter(({ name }) => name === editData.requestMethod),
        restrictAccessBy: this.restrictAccessByDropdownOptions.filter(
          ({ id }) => id === (editData.authorization?.scope ?? ERestrictAccessBy.none),
        ),
        resultType: this.resultTypeDropdownOptions.filter(({ id }) => id === editData.resultType),
        workspaceId:
          editData.authorization?.scope === ERestrictAccessBy.workspace
            ? editData.authorization.ids.map((id) => ({ id, name: '...' }))
            : [],
      });
    }

    this.store.loadDataSources({ limit: 1000 });

    this.dashboardSearch.pipe(takeUntilDestroyed()).subscribe((search) => {
      this.store.loadDashboards({
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      });
    });

    this.workspaceSearch.pipe(takeUntilDestroyed()).subscribe((search) => {
      this.store.loadWorkspaces({
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      });
    });

    this.store.afterCreateUpdateSuccess$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.matDialogRef.close();
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const payload = this.formatOnePayload();

    if (this.injectData.editData?.id) {
      this.store.updateOne({ dto: payload, id: this.injectData.editData.id });
    } else {
      this.store.createOne(payload);
    }
  }

  private formatOnePayload(): IWidgetRequestAddPayload {
    const { value } = this.addEditForm;
    const restrictAccessById = value.restrictAccessBy![0].id;
    const resultType = value.resultType![0].id;

    return {
      authorization:
        restrictAccessById === ERestrictAccessBy.none
          ? null
          : {
              ids:
                restrictAccessById === ERestrictAccessBy.workspace
                  ? value.workspaceId!.map(({ id }) => id)
                  : value.dashboardId!.map(({ id }) => id),
              scope: restrictAccessById,
            },
      content: FormUtilitiesService.trimIfString(value.content!),
      dataSourceId: value.dataSourceId![0].id,
      endpoint: FormUtilitiesService.trimIfString(value.endpoint!),
      name: FormUtilitiesService.trimIfString(value.name!),
      requestMethod: value.requestMethod![0].name as 'POST' | 'GET',
      resultType,
    };
  }

  private availabilityOfRestrictAccessInputsSubscription(): void {
    this.addEditForm.controls.restrictAccessBy.valueChanges
      .pipe(startWith(this.addEditForm.controls.restrictAccessBy.value))
      .subscribe((restrictAccessBy) => {
        if (!restrictAccessBy?.length || restrictAccessBy[0].id === ERestrictAccessBy.none) {
          this.addEditForm.controls.dashboardId.disable();
          this.addEditForm.controls.workspaceId.disable();

          return;
        }

        if (restrictAccessBy[0].id === ERestrictAccessBy.dashboard) {
          this.addEditForm.controls.dashboardId.enable();
          this.addEditForm.controls.workspaceId.disable();

          return;
        }

        this.addEditForm.controls.dashboardId.disable();
        this.addEditForm.controls.workspaceId.enable();
      });
  }

  private availabilityOfContentPathInputSubscription(): void {
    this.addEditForm.controls.resultType.valueChanges
      .pipe(
        startWith(this.addEditForm.controls.resultType.value),
        map((resultType) => (resultType?.length ? resultType[0].id : null)),
        distinctUntilChanged(),
        pairwise(),
      )
      .subscribe(([old, resultType]) => {
        if (old !== null) {
          this.addEditForm.controls.content.reset('');
        }

        if (resultType === EWidgetRequestResultType.singleField) {
          this.addEditForm.controls.content.setValidators([
            Validators.required,
            TrimmedRequiredValidator(),
            Validators.maxLength(255),
          ]);

          return;
        }

        this.addEditForm.controls.content.setValidators([Validators.maxLength(255)]);
      });
  }
}
